import React, { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { styled } from "styled-components";

import CommunicationAnswerDetail from "pages/communication/components/CommunicationAnswerDetail";
import { CommunicationStatusBadge } from "pages/communication/components/CommunicationStatusBadge";
import { splitContentImage } from "pages/communication/utils/splitContentImage";
import {
  answerCommunication,
  deleteCommunication,
  getCommunicationDetail,
  updateCommunicationStatus,
} from "shared/api/InquirySuggestApi";
import { formatToKST } from "shared/lib/date/formatKst";
import { alertDialog, confirmDialog, confirmLeaveIfDirty } from "shared/ui/feedback";

const StatusWrap = styled.div`
  margin-left: 25px;
  margin-top: 18px;
`;

function renderStatusBadge(statusType) {
  return (
    <StatusWrap>
      <CommunicationStatusBadge type={statusType} />
    </StatusWrap>
  );
}

function CommunicationAnswerPage({ config }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState({});
  const [content, setContent] = useState("");
  const [img, setImg] = useState("");
  const [answer, setAnswer] = useState("");
  const [initialAnswer, setInitialAnswer] = useState("");

  useEffect(() => {
    const readDetail = async () => {
      try {
        const detail = await getCommunicationDetail(config.detailPath, id);
        const { image, content: parsedContent } = splitContentImage(
          detail.content,
        );

        setData(detail);
        setAnswer(detail.answer ?? "");
        setInitialAnswer(detail.answer ?? "");
        setImg(image);
        setContent(parsedContent);
      } catch (error) {
      }
    };

    readDetail();
  }, [config, id]);

  // 이전 핸들러: 답변 내용이 변경됐을 때만 확인 모달을 띄운다.
  const handleBack = async () => {
    const shouldLeave = await confirmLeaveIfDirty(
      answer !== initialAnswer,
      "작성 중인 답변이 사라집니다.\n이전 화면으로 이동하시겠습니까?",
    );
    if (shouldLeave) {
      navigate(-1);
    }
  };

  const moveToList = () => {
    navigate(config.listPath);
  };

  const deleteData = async () => {
    try {
      await deleteCommunication(config.detailPath, id);
      moveToList();
    } catch (error) {
    }
  };

  const updateStatus = async () => {
    try {
      await updateCommunicationStatus(config.detailPath, id, config.progressStatus);
      moveToList();
    } catch (error) {
    }
  };

  const submitAnswer = async () => {
    if (answer === "") {
      alertDialog("답변을 입력해주세요");
      return;
    }

    if (answer.length > 5000) {
      alertDialog("답변은 5000자 제한입니다.");
      return;
    }

    try {
      await answerCommunication(config.detailPath, id, {
        ansUserId: 1,
        answer,
      });
      moveToList();
    } catch (error) {
    }
  };

  const handleDeleteClick = async () => {
    const shouldProceed = await confirmDialog(config.deleteConfirmMessage);

    if (shouldProceed) {
      deleteData();
    }
  };

  const handleProgressClick = async () => {
    const shouldProceed = await confirmDialog(config.progressConfirmMessage);

    if (shouldProceed) {
      updateStatus();
    }
  };

  const handleAnswerClick = async () => {
    const confirmMessage =
      data.status === config.doneStatus
        ? config.editConfirmMessage
        : config.answerConfirmMessage;
    const shouldProceed = await confirmDialog(confirmMessage);

    if (shouldProceed) {
      submitAnswer();
    }
  };

  const answerButtonLabel =
    data.status === config.doneStatus ? "수정하기" : "답변하기";

  return (
    <CommunicationAnswerDetail
      title={config.title}
      boardTitle={data.title}
      userText={config.getUserText(data)}
      postedAt={formatToKST(data.createdAt)}
      viewCount={data.viewCnt}
      statusBadge={renderStatusBadge(config.getStatusBadgeType(data.status))}
      content={content}
      imageSrc={img}
      answer={answer}
      onAnswerChange={setAnswer}
      warningMessages={config.warningMessages}
      deleteLabel={config.deleteLabel}
      onDelete={handleDeleteClick}
      progressLabel={config.progressLabel}
      onProgress={handleProgressClick}
      showProgressButton={data.status === config.waitingStatus}
      answerButtonLabel={answerButtonLabel}
      onAnswerSubmit={handleAnswerClick}
      onBack={handleBack}
    />
  );
}

export default CommunicationAnswerPage;
